"use client";
import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Ticket,
  Eye,
  Shield,
  MessageSquare,
  User,
} from "lucide-react";
import { useSupportTickets } from "../context/SupportTicketContext";

const STATUS_OPTIONS = ["Pending", "In Progress", "Resolved", "Closed"];


const cx = (...c) => c.filter(Boolean).join(" ");

const Pill = ({ children, tone = "gray", size = "md" }) => {
  const toneMap = {
    red: "bg-red-50 text-red-700 ring-red-100",
    blue: "bg-blue-50 text-blue-700 ring-blue-100",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-100",
    gray: "bg-gray-50 text-gray-700 ring-gray-200",
    amber: "bg-amber-50 text-amber-700 ring-amber-100",
    violet: "bg-violet-50 text-violet-700 ring-violet-100",
    yellow: "bg-yellow-50 text-yellow-700 ring-yellow-100",
    orange: "bg-orange-50 text-orange-700 ring-orange-100",
  };
  const sizeMap = {
    sm: "px-2.5 py-1 text-[11px]",
    md: "px-3 py-1.5 text-xs",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full font-extrabold ring-1 whitespace-nowrap",
        toneMap[tone] || toneMap.gray,
        sizeMap[size] || sizeMap.md
      )}
    >
      {children}
    </span>
  );
};

const TicketDetailModal = ({ ticket, isOpen, onClose }) => {
  const { updateTicketStatus } = useSupportTickets();
  const [localStatus, setLocalStatus] = useState(ticket?.status || "Pending");
  const [updating, setUpdating] = useState(false);

  if (!isOpen || !ticket) return null;

  const handleStatusChange = (e) => {
    setLocalStatus(e.target.value);
  };

  const handleSubmit = async () => {
    setUpdating(true);
    const success = await updateTicketStatus(ticket.id, localStatus);
    setUpdating(false);

    if (success) {
      onClose(); // Close modal after successful update
    } else {
      alert("Failed to update status");
      setLocalStatus(ticket.status); // Rollback
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-3xl p-8 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-extrabold text-gray-900">Ticket Details</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-2xl hover:bg-gray-100 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-extrabold text-gray-700 mb-2">Subject</label>
            <div className="p-3 bg-gray-50 rounded-2xl">{ticket.subject || "N/A"}</div>
          </div>

          <div>
            <label className="block text-sm font-extrabold text-gray-700 mb-2">Description</label>
            <div className="p-3 bg-gray-50 rounded-2xl min-h-[100px]">{ticket.description || "N/A"}</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-extrabold text-gray-700 mb-2">Status</label>
              <select
                value={localStatus}
                onChange={handleStatusChange}
                disabled={updating}
                className="p-2 bg-gray-50 rounded-2xl w-full"
              >
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex mt-4">
                <button
                  onClick={handleSubmit}
                  disabled={updating}
                  className="px-6 py-2 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {updating ? "Updating..." : "Update Status"}
                </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-extrabold text-gray-700 mb-2">Priority</label>
              <Pill tone={getPriorityTone(ticket.priority)}>{ticket.priority || "Medium"}</Pill>
            </div>

            <div>
              <label className="block text-sm font-extrabold text-gray-700 mb-2">User</label>
              <div className="text-sm text-gray-600">
                {ticket.user_name || ticket.user_email || "Unknown User"}
              </div>
            </div>
          </div>
          
          {/* ✅ Submit Button */}
          
        </div>
      </div>
    </div>
  );
};


const getStatusTone = (status) => {
  switch (status) {
    case "Open": return "blue";
    case "In Progress": return "amber";
    case "Resolved": return "green";
    case "Closed": return "gray";
    default: return "blue";
  }
};

const getPriorityTone = (priority) => {
  switch (priority) {
    case "Low": return "green";
    case "Medium": return "amber";
    case "High": return "orange";
    case "Critical": return "red";
    default: return "amber";
  }
};

const SectionDivider = ({ label, right }) => (
  <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
    <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wider">
      {label}
    </h3>
    {right}
  </div>
);


export default function SupportTicketsPage() {
  const { supportTickets, loading, error, fetchSupportTickets } = useSupportTickets();

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    fetchSupportTickets();
  }, []);

  const filteredTickets = useMemo(() => {
    if (!search.trim()) return supportTickets;
    return supportTickets.filter(
      (ticket) =>
        ticket.subject?.toLowerCase().includes(search.toLowerCase()) ||
        ticket.description?.toLowerCase().includes(search.toLowerCase()) ||
        ticket.user_name?.toLowerCase().includes(search.toLowerCase())
    );
  }, [supportTickets, search]);

  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
    const start = (page - 1) * ITEMS_PER_PAGE;
    const currentTickets = filteredTickets.slice(start, start + ITEMS_PER_PAGE);
    return { totalPages, currentTickets };
  }, [filteredTickets, page]);

  const stats = useMemo(() => ({
    total: filteredTickets.length,
    open: filteredTickets.filter(t => t.status === "Open" || !t.status).length,
    resolved: filteredTickets.filter(t => t.status === "Resolved").length
  }), [filteredTickets]);

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailModal(true);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb] flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Shield className="h-6 w-6 text-red-600" />
          </div>
          <h2 className="text-xl font-extrabold text-gray-900 mb-2">Error Loading Tickets</h2>
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchSupportTickets}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#fff7f6] to-[#f9fafb]">
      <div className="p-6 sm:p-8">
        {/* HERO SECTION */}
        <div className="rounded-3xl bg-white shadow-sm ring-1 ring-gray-200 overflow-hidden mb-8">
          <div className="relative p-7 sm:p-10">
            <div className="pointer-events-none absolute -top-28 -left-28 h-72 w-72 rounded-full bg-blue-200/25 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-28 -right-28 h-72 w-72 rounded-full bg-green-200/25 blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tone="blue">
                    <Ticket className="h-4 w-4 mr-1" />
                    Support Tickets
                  </Pill>
                  <Pill tone="gray">Admin Portal</Pill>
                  <Pill tone="green">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Customer Support
                  </Pill>
                </div>

                <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-gray-900">
                  Support Tickets
                </h1>
                <p className="mt-3 text-gray-600 text-sm sm:text-base">
                  View and monitor customer support requests and ticket details.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-gray-50 ring-1 ring-gray-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-gray-600">Total</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {stats.total} tickets
                  </div>
                </div>

                <div className="rounded-2xl bg-blue-50 ring-1 ring-blue-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-blue-600">Open</div>
                  <div className="text-sm font-semibold text-blue-900 mt-1">
                    {stats.open} tickets
                  </div>
                </div>

                <div className="rounded-2xl bg-green-50 ring-1 ring-green-200 px-4 py-3">
                  <div className="text-[11px] font-extrabold tracking-widest uppercase text-green-600">Resolved</div>
                  <div className="text-sm font-semibold text-green-900 mt-1">
                    {stats.resolved} tickets
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-between items-center mb-4 border-b border-gray-200 pb-2">
  <h3 className="text-sm font-extrabold text-gray-900 uppercase tracking-wider">
    Browse Tickets
  </h3>
  <div className="flex items-center gap-2">
    {search && <Pill tone="amber" size="sm">Search: {search}</Pill>}
    <Pill tone="blue" size="sm">Page {page} of {paginationData.totalPages}</Pill>
  </div>
</div>


        {/* SEARCH */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 rounded-2xl ring-1 ring-gray-200 focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="rounded-3xl bg-white ring-1 ring-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Ticket
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Priority
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-extrabold tracking-widest uppercase text-gray-600">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-600 border-t-transparent"></div>
                        <span className="text-sm text-gray-600">Loading tickets...</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  <>
                    {paginationData.currentTickets.map((ticket, index) => (
                      <tr
                        key={ticket.id}
                        className={cx(
                          "hover:bg-gray-50 transition-colors",
                          index !== paginationData.currentTickets.length - 1 ? "border-b border-gray-100" : ""
                        )}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-2xl bg-blue-100 flex items-center justify-center">
                              <MessageSquare className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <div className="font-extrabold text-gray-900">
                                {ticket.subject || `Ticket #${ticket.id}`}
                              </div>
                              <div className="text-sm text-gray-600 truncate max-w-[200px]">
                                {ticket.description || "No description"}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-2xl bg-gray-100 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                            <div className="font-medium text-gray-700">
                              {ticket.user_name || ticket.user_email || "Unknown User"}
                            </div>
                          </div>
                        </td>

                        <td className="px-6 py-4">
                          <Pill
                            tone={getStatusTone(ticket.status)}
                            size="sm"
                          >
                            {ticket.status || "Open"}
                          </Pill>
                        </td>

                        <td className="px-6 py-4">
                          <Pill
                            tone={getPriorityTone(ticket.priority)}
                            size="sm"
                          >
                            {ticket.priority || "Medium"}
                          </Pill>
                        </td>

                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewTicket(ticket)}
                              className="p-2 rounded-2xl hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                              aria-label="View ticket"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}

                    {paginationData.currentTickets.length === 0 && (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center">
                          <div className="flex flex-col items-center gap-3">
                            <div className="h-12 w-12 rounded-full bg-gray-100 flex items-center justify-center">
                              <Ticket className="h-6 w-6 text-gray-400" />
                            </div>
                            <div>
                              <div className="font-extrabold text-gray-900">No tickets found</div>
                              <p className="mt-1 text-sm text-gray-600">
                                {search ? "Try adjusting your search terms." : "No support tickets have been created yet."}
                              </p>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* PAGINATION */}
        {paginationData.totalPages > 1 && (
          <div className="flex justify-center items-center gap-3 mt-8">
            <button
              onClick={() => setPage(Math.max(page - 1, 1))}
              disabled={page === 1}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === 1
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Previous page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: paginationData.totalPages }, (_, i) => i + 1).map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={cx(
                    "px-4 py-2 rounded-2xl text-sm font-extrabold transition-colors",
                    pageNum === page
                      ? "bg-red-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50 ring-1 ring-gray-200"
                  )}
                >
                  {pageNum}
                </button>
              ))}
            </div>

            <button
              onClick={() => setPage(Math.min(page + 1, paginationData.totalPages))}
              disabled={page === paginationData.totalPages}
              className={cx(
                "p-3 rounded-2xl ring-1 ring-gray-200 transition-colors",
                page === paginationData.totalPages
                  ? "opacity-50 cursor-not-allowed bg-gray-50"
                  : "bg-white hover:bg-gray-50"
              )}
              aria-label="Next page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      <TicketDetailModal
        ticket={selectedTicket}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
      />
    </div>
  );
}
